import React from "react";
import { useNavigate } from "react-router-dom";
import { Heading } from "../design/heading";
import { Text } from "../design/text";
import { Link } from "../design/link";
import { BuildingOffice2Icon, UserGroupIcon } from "@heroicons/react/24/outline";

import logo from "../assets/logo.png";

const SignupPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-zinc-900 dark:via-zinc-800 dark:to-zinc-900">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-200 rounded-full opacity-20 blur-3xl dark:bg-blue-900" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-indigo-200 rounded-full opacity-20 blur-3xl dark:bg-indigo-900" />
      </div>

      <div className="relative w-full max-w-2xl px-4">
        <div className="w-full p-8 bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl border border-white/20 dark:bg-zinc-900/80 dark:border-zinc-700/50">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <img src={logo} alt="Visit Proof" className="w-20 h-20 mx-auto mb-4" />
            <Heading className="text-2xl font-bold text-zinc-900 dark:text-white">
              Get Started with VisitProof
            </Heading>
            <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
              Choose how you'd like to use VisitProof
            </Text>
          </div>

          {/* Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8">
            {/* Create Organization Option */}
            <button
              onClick={() => navigate("/register")}
              className="group relative flex flex-col items-center p-6 bg-white rounded-xl border-2 border-zinc-200 hover:border-blue-500 hover:shadow-lg transition-all duration-200 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:border-blue-400"
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-blue-100 text-blue-600 group-hover:bg-blue-500 group-hover:text-white transition-colors dark:bg-blue-900/50 dark:text-blue-400">
                <BuildingOffice2Icon className="w-8 h-8" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Create an Organization
              </h3>
              <p className="mt-2 text-sm text-center text-zinc-600 dark:text-zinc-400">
                I'm a provider setting up a new supervised visitation business
              </p>
              <span className="mt-4 text-sm font-medium text-blue-600 group-hover:text-blue-700 dark:text-blue-400">
                Set up my business →
              </span>
            </button>

            {/* Join Organization Option */}
            <button
              onClick={() => navigate("/signup/join")}
              className="group relative flex flex-col items-center p-6 bg-white rounded-xl border-2 border-zinc-200 hover:border-green-500 hover:shadow-lg transition-all duration-200 dark:bg-zinc-800 dark:border-zinc-700 dark:hover:border-green-400"
            >
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-100 text-green-600 group-hover:bg-green-500 group-hover:text-white transition-colors dark:bg-green-900/50 dark:text-green-400">
                <UserGroupIcon className="w-8 h-8" />
              </div>
              <h3 className="mt-4 text-lg font-semibold text-zinc-900 dark:text-white">
                Join an Organization
              </h3>
              <p className="mt-2 text-sm text-center text-zinc-600 dark:text-zinc-400">
                I've been invited to join an existing organization as staff or supervisor
              </p>
              <span className="mt-4 text-sm font-medium text-green-600 group-hover:text-green-700 dark:text-green-400">
                Accept my invitation →
              </span>
            </button>
          </div>

          {/* Login Link */}
          <div className="mt-8 text-center">
            <Text className="text-zinc-600 dark:text-zinc-400">
              Already have an account?{" "}
              <Link href="/login" className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400">
                Sign in
              </Link>
            </Text>
          </div>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-xs text-zinc-500 dark:text-zinc-500">
          By signing up, you agree to our{" "}
          <a href="/terms" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy" className="underline hover:text-zinc-700 dark:hover:text-zinc-300">
            Privacy Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default SignupPage;
